import React, { useContext, useEffect, useState } from "react";

import { useHotkeys } from "react-hotkeys-hook";

import { MapContext } from "../Map";

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers

  return distance * 1000;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

export default function TillerLayer({
  id,
  bounds,
  minZoom,
  maxZoom,
  tilesUrl,
  seamlinesUrl,
  gcps,
}) {
  const map = useContext(MapContext);

  const [showRoads, setShowRoads] = useState(false);
  const [showSeamlines, setShowSeamlines] = useState(false);
  const [coverageOpacity, setCoverageOpacity] = useState(1);
  const [clicks, setClicks] = useState([]);
  const [lastClick, setLastClick] = useState(null);
  const [selectedGcpIndex, setSelectedGcpIndex] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (!map) return;
    if (zoomed) return;

    map.on("zoom", (e) => {
      setZoomed(true);
    });
  }, [map, zoomed]);

  useEffect(() => {
    if (!map) return;
    if (selectedGcpIndex == null) return;

    setShowRoads(true);

    let options = {
      center: [gcps[selectedGcpIndex].lng, gcps[selectedGcpIndex].lat],
    };
    if (!zoomed) {
      options.zoom = 14;
    }

    console.log(options);

    map.jumpTo(options);
  }, [selectedGcpIndex, zoomed]);

  useEffect(() => {
    if (!map) return;

    if (clicks.length % 2 === 0) {
      // get last two coordinates and calc distance using `calculateDistance`:
      let dist = calculateDistance(
        clicks[clicks.length - 2].lat,
        clicks[clicks.length - 2].lng,
        clicks[clicks.length - 1].lat,
        clicks[clicks.length - 1].lng
      );
      console.log(dist);
    }
  }, [clicks]);

  useEffect(() => {
    if (lastClick) {
      setClicks([...clicks, lastClick]);
    }
  }, [lastClick]);

  useEffect(() => {
    if (!map) return;

    map.on("click", (e) => {
      let f = map.unproject(e.point);
      let lat = f.lat.toFixed(6);
      let lng = f.lng.toFixed(6);
      let coords = `${lat},${lng}`;

      setLastClick(f);

      console.log(coords);

      navigator.clipboard.writeText(coords).then(() => {});
    });

    map.on("click", (e) => {
      let features = map.queryRenderedFeatures(e.point, {
        layers: ["seamlines"],
      });

      if (features.length) {
        let clickedFeature = features[0];
        if (clickedFeature.properties.NAME) {
          window.open(`/usgs/gcps/${clickedFeature.properties.NAME}`);
        }
      }
    });

    map.on("boxzoomend", (e) => {
      let f = map.getBounds();
      let bounds = `${f.getWest()},${f.getSouth()},${f.getEast()},${f.getNorth()}`;

      navigator.clipboard.writeText(bounds).then(
        function () {
          alert("Copied BBOX to clipboard: " + bounds);
        },
        function (err) {
          alert("Error copyuing BBOX to clipboard: " + err);
        }
      );

      console.log({ f, bounds });
    });

    map.addSource(`tiller-${id}`, {
      type: "raster",
      scheme: "tms",
      tiles: [tilesUrl],
      minzoom: minZoom,
      maxzoom: maxZoom,
      tileSize: 128, // 256 if not retina
      bounds,
    });

    map.addLayer(
      {
        id: `raster-${id}`,
        type: "raster",
        source: `tiller-${id}`,
        paint: {
          "raster-fade-duration": 150,
        },
      },
      "satellite-tunnel"
    );

    map.setPaintProperty(
      "satellite-road",
      "line-color",
      "rgba(255, 0, 0, 0.5)"
    );
  }, [map]);

  useEffect(() => {
    if (!map) return;
    if (!gcps) return;

    map.addSource("gcps", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: gcps.map((gcp) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [gcp.lng, gcp.lat],
          },
          properties: {
            id: gcp.id,
          },
        })),
      },
    });

    map.addLayer({
      id: "gcps-marker",
      type: "circle",
      source: "gcps",
      paint: {
        "circle-color": "#f00",
        "circle-radius": 4,
        "circle-stroke-width": 1,
        "circle-stroke-color": "#fff",
      },
    });

    map.addLayer({
      id: "gcps-label",
      type: "symbol",
      source: "gcps",
      paint: {
        "text-color": "#f00",
        "text-halo-color": "#fff",
        "text-halo-width": 1,
      },
      layout: {
        "text-field": "{id}",
        "text-font": ["Helvetica Bold"],
        "text-size": 12,
        "text-offset": [0, 1],
      },
    });
  }, [map, gcps]);

  useEffect(() => {
    if (!map) return;
    if (!seamlinesUrl) return;

    console.log(seamlinesUrl);

    map.addSource("seamlines", {
      type: "geojson",
      data: seamlinesUrl,
    });

    map.addLayer({
      id: "seamlines-labels",
      type: "symbol",
      source: "seamlines",
      layout: {
        "text-font": ["Open Sans Regular"],
        "text-field": "{NAME}",
        "text-size": 16,
      },
      paint: {
        "text-color": "#00ff00",
        "text-halo-color": "#000000",
        "text-halo-width": 1,
      },
    });

    map.addLayer({
      id: "seamlines",
      type: "fill",
      source: "seamlines",
      paint: {
        "fill-color": "#00ff00",
        "fill-opacity": 0,
      },
    });

    map.addLayer({
      id: "seamlines-line",
      type: "line",
      source: "seamlines",
      paint: {
        "line-color": "#00ff00",
      },
    });
  }, [map, seamlinesUrl]);

  useEffect(() => {
    if (!map) return;

    map.setPaintProperty(`raster-${id}`, "raster-opacity", coverageOpacity);
  }, [map, coverageOpacity]);

  useEffect(() => {
    if (!map) return;

    let layers = ["satellite-road"];
    if (gcps) layers.push("gcps-marker", "gcps-label");

    for (const layer of layers) {
      map.setLayoutProperty(
        layer,
        "visibility",
        showRoads ? "visible" : "none"
      );
    }
  }, [map, showRoads]);

  useEffect(() => {
    if (!map) return;

    let layers = [];
    if (seamlinesUrl)
      layers.push("seamlines", "seamlines-line", "seamlines-labels");

    for (const layer of layers) {
      map.setLayoutProperty(
        layer,
        "visibility",
        showSeamlines ? "visible" : "none"
      );
    }
  }, [map, showSeamlines]);

  useHotkeys("0", () => {
    setCoverageOpacity((previousCoverageOpacity) =>
      previousCoverageOpacity == 0 ? 1 : 0
    );
  });

  useHotkeys("l", () => {
    setShowRoads((previousShowRoads) => !previousShowRoads);
  });

  useHotkeys("s", () => {
    setShowSeamlines((previousShowSeamlines) => !previousShowSeamlines);
  });

  useHotkeys(
    "]",
    () => {
      if (!gcps) return;
      if (gcps.length == 0) return;

      if (selectedGcpIndex == null) {
        setSelectedGcpIndex(0);
      } else {
        setSelectedGcpIndex((previousSelectedGcpIndex) => {
          if (previousSelectedGcpIndex == gcps.length - 1) {
            return 0;
          } else {
            return previousSelectedGcpIndex + 1;
          }
        });
      }
    },
    [selectedGcpIndex]
  );

  useHotkeys(
    "[",
    () => {
      if (!gcps) return;
      if (gcps.length == 0) return;

      if (selectedGcpIndex == null) {
        setSelectedGcpIndex(gcps.length - 1);
      } else {
        setSelectedGcpIndex((previousSelectedGcpIndex) => {
          if (previousSelectedGcpIndex == 0) {
            return gcps.length - 1;
          } else {
            return previousSelectedGcpIndex - 1;
          }
        });
      }
    },
    [selectedGcpIndex]
  );

  return <></>;
}
